import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import type {
  FaqSupportRequest,
  PaginatedFaqSupportRequests,
} from '../../types/knowledgeQuestions';
import {
  Button,
  Callout,
  Dialog,
  EmptyState,
  Field,
  IconNote,
  Input,
  ListPage,
  Pagination,
  Row,
  SectionTitle,
  Segmented,
  StatusLabel,
  Table,
  TableSkeletonRows,
  TBody,
  TD,
  Textarea,
  TH,
  THead,
  TR,
  TwoLine,
  When,
} from '../../ui/v2';
import { apiEndpoints } from '../../utils/apiService';
import { getJson, send } from '../KnowledgeQuestions/knowledgeData';

const PAGE_SIZE = 20;
const QUERY_KEY = 'admin-faq-support';

type Filter = 'open' | 'resolved';
const FILTER_ITEMS: { key: Filter; label: string }[] = [
  { key: 'open', label: 'Open' },
  { key: 'resolved', label: 'Resolved' },
];

/**
 * Additional FAQs the conflict check flagged and stores sent to us. Approving
 * marks the answer checked so a manager can publish it; declining returns it
 * to draft with the note shown to the store.
 */
export default function FaqSupportTab() {
  const [filter, setFilter] = useState<Filter>('open');
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<number | null>(null);

  const list = useQuery({
    queryKey: [QUERY_KEY, filter, page],
    queryFn: () =>
      getJson<PaginatedFaqSupportRequests>(
        apiEndpoints.adminFaqSupport({
          status: filter,
          page,
          page_size: PAGE_SIZE,
        }),
        'Support requests could not be loaded.',
      ),
    placeholderData: keepPreviousData,
  });
  const rows = list.data?.results ?? [];
  const loading = list.isPending;
  const selected = rows.find((r) => r.id === openId) ?? null;

  return (
    <>
      <ListPage
        title="FAQ support"
        toolbar={
          <div className="flex min-h-11 flex-wrap items-center gap-3 px-4 py-2 lg:px-6">
            <Segmented
              aria-label="Request status"
              items={FILTER_ITEMS}
              value={filter}
              onChange={(next) => {
                setFilter(next);
                setPage(1);
              }}
              className="shrink-0"
            />
          </div>
        }
      >
        {list.isError ? (
          <div className="p-4 lg:px-6">
            <Callout tone="danger" title={(list.error as Error).message} />
          </div>
        ) : !loading && rows.length === 0 ? (
          <EmptyState
            icon={<IconNote />}
            title={
              filter === 'open'
                ? 'No FAQs waiting on support'
                : 'No resolved requests yet'
            }
            description="When a store sends a flagged FAQ, it shows up here."
          />
        ) : (
          <div className="v2-scroll overflow-x-auto">
            <Table fixed className="min-w-[860px]">
              <THead>
                <tr>
                  <TH width={220}>Store</TH>
                  <TH>Question</TH>
                  <TH width={180}>Sent</TH>
                  {filter === 'resolved' && <TH width={140}>Outcome</TH>}
                </tr>
              </THead>
              <TBody>
                {loading ? (
                  <TableSkeletonRows cols={filter === 'resolved' ? 4 : 3} />
                ) : (
                  rows.map((row) => (
                    <TR
                      key={row.id}
                      selected={openId === row.id}
                      onActivate={() => setOpenId(row.id)}
                    >
                      <TD truncate>
                        <TwoLine
                          primary={row.store.name}
                          secondary={row.created_by}
                        />
                      </TD>
                      <TD truncate className="text-v2-fg-2">
                        {row.faq.question}
                      </TD>
                      <TD>
                        <When value={row.created_at} />
                      </TD>
                      {filter === 'resolved' && (
                        <TD>
                          <ResolutionLabel request={row} />
                        </TD>
                      )}
                    </TR>
                  ))
                )}
              </TBody>
            </Table>
          </div>
        )}
        <Pagination
          page={page}
          totalPages={list.data?.total_pages ?? 1}
          totalCount={list.data?.count ?? 0}
          pageSize={PAGE_SIZE}
          onPage={setPage}
          noun="requests"
        />
      </ListPage>

      <SupportRequestDialog
        request={selected}
        onClose={() => setOpenId(null)}
      />
    </>
  );
}

const ResolutionLabel: React.FC<{ request: FaqSupportRequest }> = ({
  request,
}) =>
  request.resolution === 'approved' ? (
    <StatusLabel size="sm" tone="success">
      Approved
    </StatusLabel>
  ) : (
    <StatusLabel size="sm" tone="neutral" hollow>
      Declined
    </StatusLabel>
  );

const SupportRequestDialog: React.FC<{
  request: FaqSupportRequest | null;
  onClose: () => void;
}> = ({ request, onClose }) => {
  const queryClient = useQueryClient();
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState<'approved' | 'declined' | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setQuestion(request?.faq.question ?? '');
    setAnswer(request?.faq.answer ?? '');
    setNote('');
    setError(null);
  }, [request?.id, request?.faq.question, request?.faq.answer]);

  if (!request) return null;
  const isOpen = request.status === 'open';
  const snapshot = request.check_snapshot;

  const resolve = async (resolution: 'approved' | 'declined') => {
    setBusy(resolution);
    setError(null);
    const body: Record<string, string> = { resolution, note: note.trim() };
    if (resolution === 'approved') {
      if (question.trim() !== request.faq.question) {
        body.question = question.trim();
      }
      if (answer.trim() !== request.faq.answer) body.answer = answer.trim();
    }
    try {
      await send(
        'post',
        apiEndpoints.adminFaqSupportResolve(request.id),
        body,
        'The request could not be resolved.',
      );
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      dismissible={busy == null}
      size="lg"
      title={request.store.name}
      description={`Sent by ${request.created_by || 'store staff'}`}
      footer={
        isOpen ? (
          <>
            <Button
              variant="secondary"
              loading={busy === 'declined'}
              disabled={busy === 'approved'}
              disabledReason={
                note.trim() ? undefined : 'Add a note for the store first.'
              }
              onClick={() => void resolve('declined')}
            >
              Decline
            </Button>
            <Button
              variant="primary"
              loading={busy === 'approved'}
              disabled={busy === 'declined'}
              disabledReason={
                question.trim() && answer.trim()
                  ? undefined
                  : 'The question and answer can’t be empty.'
              }
              onClick={() => void resolve('approved')}
            >
              Approve
            </Button>
          </>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-5">
        <section aria-label="Message">
          <SectionTitle>Message from the store</SectionTitle>
          <p className="mt-1 whitespace-pre-wrap text-v2-13 text-v2-fg-2">
            {request.message}
          </p>
        </section>

        <section aria-label="Conflict check">
          <SectionTitle>What the check found</SectionTitle>
          {snapshot.summary && (
            <p className="mt-1 text-v2-13 text-v2-fg-2">{snapshot.summary}</p>
          )}
          {(snapshot.citations ?? []).length > 0 && (
            <ul className="mt-2 flex flex-col gap-1.5" aria-label="Sources">
              {(snapshot.citations ?? []).map((c, i) => (
                <li key={`${c.source_id}:${i}`} className="text-v2-12">
                  <span className="font-semibold text-v2-fg-2">
                    {c.source_label}
                  </span>
                  <q className="ml-1.5 text-v2-fg-3">{c.quote}</q>
                </li>
              ))}
            </ul>
          )}
        </section>

        {isOpen ? (
          <section aria-label="Resolve" className="flex flex-col gap-4">
            <Field
              label="Question"
              hint="Edit before approving if the store’s wording needs a fix."
            >
              {(id, describedBy) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  value={question}
                  maxLength={500}
                  onChange={(e) => setQuestion(e.target.value)}
                />
              )}
            </Field>
            <Field label="Answer">
              {(id, describedBy) => (
                <Textarea
                  id={id}
                  aria-describedby={describedBy}
                  rows={4}
                  value={answer}
                  maxLength={2000}
                  onChange={(e) => setAnswer(e.target.value)}
                />
              )}
            </Field>
            <Field
              label="Note for the store"
              optional
              hint="Required to decline. The store sees it above their answer."
            >
              {(id, describedBy) => (
                <Textarea
                  id={id}
                  aria-describedby={describedBy}
                  rows={2}
                  value={note}
                  maxLength={2000}
                  onChange={(e) => setNote(e.target.value)}
                />
              )}
            </Field>
          </section>
        ) : (
          <dl>
            <Row label="Outcome">
              <ResolutionLabel request={request} />
            </Row>
            <Row label="Resolved by">{request.resolved_by || '—'}</Row>
            <Row label="Resolved">
              <When value={request.resolved_at} />
            </Row>
            {request.resolution_note && (
              <Row label="Note">
                <span className="whitespace-pre-wrap">
                  {request.resolution_note}
                </span>
              </Row>
            )}
            <Row label="Question">{request.faq.question}</Row>
            <Row label="Answer">
              <span className="whitespace-pre-wrap">{request.faq.answer}</span>
            </Row>
          </dl>
        )}

        {error && (
          <Callout tone="danger" title="That didn’t go through">
            {error}
          </Callout>
        )}
      </div>
    </Dialog>
  );
};
